
// useProjectsStore — a Zustand store for the unified "Projects" list.
//
// The list is the union of up to three sources:
//   1. projects the user OWNS                (/api/v1/rfp/folders/)
//   2. projects SHARED WITH them             (/api/v1/org/folders/shared-with-me/)
//   3. for ORG ADMINS, the projects owned by other members of the same org,
//      returned by (1) under ?include_org_projects=true — visible for oversight,
//      with no permissions attached until the owner shares them.
//
// (1) and (2) can never overlap — one filters on `user_id = me`, the other on
// `shared_to_id = me`. (3) is filtered server-side to exclude both. The client
// still de-duplicates by id as a safety net, because a share created between
// the two in-flight requests could otherwise produce the same project twice.
//
// Every row carries the fields the table branches on:
//   access_scope    "owned" | "shared" | "organization"
//   is_owner        true only for owned rows
//   user_permission "owner" | "editor" | "reviewer" | "none"
//   shared_by / shared_on   null unless the project was shared with the user
import { create } from "zustand";
import Cookies from "js-cookie";
import api, { getFreshAccessToken } from "../pages/chat/api";

// Highest wins when the same project arrives from more than one source.
const PERMISSION_RANK = { owner: 3, editor: 2, reviewer: 1, none: 0 };

const rankOf = (row) => PERMISSION_RANK[String(row?.user_permission || "").toLowerCase()] ?? 0;

// Owned / org-oversight project → unified row. The folders endpoint already
// returns most of the shape; we only pin down the fields the merged table
// relies on. `access_scope` distinguishes the two: the backend sends "owned"
// for the user's own projects and "organization" for admin oversight rows.
const normalizeFolderRow = (project) => {
  const scope = project.access_scope === "organization" ? "organization" : "owned";
  return {
    ...project,
    id: project.id ?? null,
    name: project.name || "_",
    status: project.rfp_status || "Draft",
    contract_value: project.contract_value || "_",
    deadline: project.deadline || "_", // Ensure deadline falls back to "_"
    date_submitted: project.date_submitted || "_", // Manually-entered submission date; "_" until set
    access_scope: scope,
    is_owner: scope === "owned",
    user_permission: scope === "owned" ? "owner" : "none",
    // Never shared *to* the current user — these cells render as "_".
    shared_by: null,
    shared_on: null,
  };
};

// Shared project → unified row. Field names differ from the folders endpoint
// (folder_id/folder_name/…), so they are mapped across explicitly.
const normalizeShared = (project) => ({
  id: project.folder_id ?? null,
  name: project.folder_name || "_",
  description: project.folder_description || "",
  status: project.rfp_status || "Draft",
  contract_value: project.contract_value || "_",
  deadline: project.deadline || "_",
  date_submitted: project.date_submitted || "_",
  project_lead: project.project_lead || null,
  note: project.note || null,
  web_links: project.web_links || [],
  tags: project.tags || [],
  shared_to: project.shared_to || [],
  created_at: project.created_at || null,
  updated_at: project.updated_at || null,
  org_id: project.org_id,
  owner: {
    name: project.owner_name || "",
    email: project.owner_email || null,
  },
  editors_count: project.editors_count,
  reviewers_count: project.reviewers_count,
  total_members: project.total_members,
  access_scope: "shared",
  is_owner: false,
  user_permission: project.permission || "reviewer",
  shared_by: project.shared_by_email || null,
  shared_on: project.shared_at || null,
});

// Collapse rows that describe the same project, keeping the one that grants the
// most access. Without this, an admin who is also an editor on a member's
// project could briefly see it twice.
const dedupeByStrongestAccess = (rows) => {
  const byId = new Map();
  rows.forEach((row) => {
    // A row with no id can't be acted on or keyed — drop it rather than let it
    // collide with other id-less rows in the map or break React's list keys.
    if (row.id === null || row.id === undefined) return;
    const existing = byId.get(row.id);
    if (!existing || rankOf(row) > rankOf(existing)) byId.set(row.id, row);
  });
  return [...byId.values()];
};

// Most-recently-edited first. The sources sort differently on their own (owned
// by created_at, shared by shared_at); one merged list needs one key, and "Last
// Edited" is the only one all of them always have.
const sortByLastEdited = (rows) =>
  [...rows].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));

// Bumped on every fetch so a slow response can never overwrite a newer one.
let requestSeq = 0;

const useProjectsStore = create((set, get) => ({
  // ── STATE
  projects: [], // the merged list of owned + shared + org-visible projects
  loading: false, // true while we are fetching (was: useState(false))
  error: null, // the last fetch error (null when fine). UI can show this later.
  // Set when one source failed but the other succeeded, so the UI can warn that
  // the list is incomplete instead of silently showing half of it.
  partialFailure: null,
  loadedOrgId: null, // which org the current `projects` belong to (org-switch safety)

  // ── ACTIONS
  // Behaves just like React's setState: you can pass a new value, OR a function
  // that receives the old value and returns the new one. This lets the existing
  // `setProjects((prev) => ...)` calls in the component keep working unchanged.
  setProjects: (updater) =>
    set((state) => ({
      projects:
        typeof updater === "function" ? updater(state.projects) : updater,
    })),

  // Asks the owner of an oversight row for access to it.
  //
  // The row is flipped to "pending" before the request goes out so the button
  // answers immediately, and rolled back if the call fails — a button that sits
  // there looking unpressed is the thing that makes people click it twice.
  requestProjectAccess: async (folderId, message) => {
    const previous = get().projects.find((p) => p.id === folderId)?.access_request_status ?? null;
    const setStatus = (value) =>
      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === folderId ? { ...p, access_request_status: value } : p
        ),
      }));

    setStatus("pending");
    try {
      const res = await api.post(`/api/v1/rfp/folders/${folderId}/request-access/`, {
        ...(message ? { message } : {}),
      });
      setStatus(res.data?.request?.status || "pending");
      return { ok: true, created: res.data?.created !== false };
    } catch (error) {
      setStatus(previous);
      return {
        ok: false,
        error:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Couldn't send the request.",
      };
    }
  },

  // Fetches every source the user can see and stores their union.
  fetchProjects: async (silent = false) => {
    const seq = ++requestSeq;
    if (!silent) set({ loading: true });
    set({ error: null, partialFailure: null });
    try {
      const token = await getFreshAccessToken();
      if (!token) return;

      const orgId = Cookies.get("selected_org_id") || null;

      // ── Edge case 1: never show another organization's projects ──────────
      // The store lives forever (it's global), so when you switch orgs the OLD
      // org's list is still sitting in memory. If the selected org changed since
      // we last loaded, drop the old list immediately — that way we never flash
      // the wrong org's data while the new list is loading.
      // If it's the SAME org, we keep the cached list on screen during refetch,
      // which gives the nice "instant render, refresh in the background" feel.
      if (get().loadedOrgId !== orgId) {
        set({ projects: [] });
      }

      const authHeader = { headers: { Authorization: `Bearer ${token}` } };
      const orgQuery = orgId ? `?organization_id=${encodeURIComponent(orgId)}` : "";

      // `include_org_projects` is only honoured for org admins — the backend
      // drops it for everyone else — so it can be sent unconditionally rather
      // than waiting on a separate role lookup that could race this fetch.
      const ownedUrl = `/api/v1/rfp/folders/${orgQuery}${orgQuery ? "&" : "?"}include_org_projects=true`;
      const sharedUrl = `/api/v1/org/folders/shared-with-me/${orgQuery}`;

      const [ownedResult, sharedResult] = await Promise.allSettled([
        api.get(ownedUrl, authHeader),
        api.get(sharedUrl, authHeader),
      ]);

      // ── Edge case 2: a stale response must never win ─────────────────────
      // Switching orgs (or any quick second refresh) starts a new fetch while
      // this one is still in flight. Whichever finishes last would otherwise
      // decide what's on screen, so anything but the newest request bails here.
      if (seq !== requestSeq) return;
      if ((Cookies.get("selected_org_id") || null) !== orgId) return;

      if (ownedResult.status === "rejected" && sharedResult.status === "rejected") {
        throw ownedResult.reason;
      }

      const ownedRows =
        ownedResult.status === "fulfilled"
          ? (ownedResult.value.data?.folders || []).map(normalizeFolderRow)
          : [];
      const sharedRows =
        sharedResult.status === "fulfilled"
          ? (sharedResult.value.data?.shared_with_me || []).map(normalizeShared)
          : [];

      // ── Edge case 3: one source failing must not blank the other ─────────
      // Owned and shared projects come from two independent services. With
      // Promise.all, a hiccup on either one would empty the whole table; with
      // allSettled the healthy half still renders and the failure is recorded
      // so the UI can say the list is incomplete.
      const failedSource =
        ownedResult.status === "rejected"
          ? "your own projects"
          : sharedResult.status === "rejected"
          ? "projects shared with you"
          : null;

      set({
        projects: sortByLastEdited(dedupeByStrongestAccess([...ownedRows, ...sharedRows])),
        loadedOrgId: orgId,
        error: ownedResult.status === "rejected" ? ownedResult.reason : null,
        partialFailure: failedSource,
      });
    } catch (error) {
      console.error("Error fetching projects:", error);
      // ── Edge case 4: don't wipe the list when a refetch fails ────────────
      // The old code did `setProjects([])` here, so a network blip after a
      // delete/rename/status change would blank the whole table even though
      // the action succeeded. Instead we KEEP whatever projects we already have
      // and just record the error so the UI can optionally surface it.
      if (seq === requestSeq) set({ error });
    } finally {
      if (!silent && seq === requestSeq) set({ loading: false });
    }
  },
}));

export default useProjectsStore;
